import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Fire, ShieldStar, CheckCircle, XCircle, Clock, CalendarBlank,
  HandHeart,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

import PlayerHeader from "@/components/player/PlayerHeader";
import PlayerStatsGrid from "@/components/player/PlayerStatsGrid";
import PlayerChartsSection from "@/components/player/PlayerChartsSection";
import PlayerMatchHistory from "@/components/player/PlayerMatchHistory";
import PlayerAchievements from "@/components/player/PlayerAchievements";
import PlayerLegacyAchievements from "@/components/player/PlayerLegacyAchievements";
import PlayerTournamentHistory from "@/components/player/PlayerTournamentHistory";
import RequestRescheduleDialog from "@/components/player/RequestRescheduleDialog";
import LogFriendlyDialog from "@/components/player/LogFriendlyDialog";
import PhotoCropDialog from "@/components/player/PhotoCropDialog";
import SubmitResultDialog from "@/components/match/SubmitResultDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_INITIAL = { opacity: 0 };
const PAGE_ANIMATE = { opacity: 1 };

export default function PlayerProfile() {
  const { playerId } = useParams();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [player, setPlayer] = useState(null);
  const [matchHistory, setMatchHistory] = useState([]);
  const [rankingHistory, setRankingHistory] = useState([]);
  const [breakStats, setBreakStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [eloHistory, setEloHistory] = useState([]);
  const [pendingValidation, setPendingValidation] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [myRescheduleRequests, setMyRescheduleRequests] = useState([]);
  const [requestMatch, setRequestMatch] = useState(null);
  const [submitMatch, setSubmitMatch] = useState(null);
  const [logFriendlyOpen, setLogFriendlyOpen] = useState(false);
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchPlayerData = useCallback(async () => {
    try {
      const [playerRes, matchHistoryRes, rankingHistoryRes, breaksRes, analyticsRes, achievementsRes, eloRes] = await Promise.all([
        axios.get(`${API}/players/${playerId}`),
        axios.get(`${API}/players/${playerId}/match-history`),
        axios.get(`${API}/players/${playerId}/ranking-history`).catch(() => ({ data: [] })),
        axios.get(`${API}/players/${playerId}/breaks`).catch(() => ({ data: null })),
        axios.get(`${API}/players/${playerId}/analytics`).catch(() => ({ data: null })),
        axios.get(`${API}/players/${playerId}/achievements`).catch(() => ({ data: { achievements: [] } })),
        axios.get(`${API}/players/${playerId}/elo-history`).catch(() => ({ data: [] })),
      ]);
      setPlayer(playerRes.data);
      setMatchHistory(matchHistoryRes.data);
      setRankingHistory(rankingHistoryRes.data);
      setBreakStats(breaksRes.data);
      setAnalytics(analyticsRes.data);
      setAchievements(achievementsRes.data?.achievements || []);
      setEloHistory(eloRes.data);
      if (user) {
        try {
          const valRes = await axios.get(`${API}/matches/pending-validation`, { withCredentials: true });
          setPendingValidation(valRes.data || []);
        } catch { /* ignore */ }
      }
      // Upcoming matches involving this player (tournament_matches returns all, filter to future/scheduled)
      try {
        const schedRes = await axios.get(`${API}/matches/scheduled`);
        const mine = (schedRes.data || []).filter(
          m => (m.player1_id === playerId || m.player2_id === playerId)
            && ["scheduled", "pending"].includes(m.status)
        );
        setUpcomingMatches(mine);
      } catch { /* ignore */ }
      // Player's own reschedule requests — only relevant for profile owner
      if (user) {
        try {
          const rrRes = await axios.get(`${API}/matches/my-reschedule-requests`, { withCredentials: true });
          setMyRescheduleRequests(rrRes.data?.requests || []);
        } catch { /* ignore */ }
      }
    } catch (error) {
      console.error("Error fetching player data:", error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  useEffect(() => { fetchPlayerData(); }, [fetchPlayerData]);

  const isProfileOwner = user && player?.claimed_by_user_id === user.user_id;
  const canUploadPhoto = user?.is_admin || isProfileOwner;

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    // Reset the input so selecting the same file twice still triggers a change.
    e.target.value = "";
    if (!file) return;
    // Client-side size guard (backend also validates). 10 MB is ample for a profile photo.
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Photo must be under 10 MB");
      return;
    }
    // Open the crop dialog — upload happens on confirm.
    setPendingPhotoFile(file);
  };

  const handleCroppedUpload = async (blob) => {
    if (!blob) return;
    setUploading(true);
    try {
      const formData = new FormData();
      // Backend infers extension from filename — use jpg for consistent output.
      const ext = blob.type === "image/png" ? "png" : "jpg";
      formData.append("file", blob, `avatar.${ext}`);
      await axios.post(`${API}/players/${playerId}/photo`, formData, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo updated");
      setPendingPhotoFile(null);
      fetchPlayerData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleValidation = async (matchId, action, reason) => {
    try {
      await axios.post(`${API}/matches/${matchId}/validate`, { action, reason }, { withCredentials: true });
      toast.success(action === "confirm" ? "Match confirmed" : "Match disputed");
      setPendingValidation(prev => prev.filter(m => m.match_id !== matchId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleTierChange = async (newTier) => {
    try {
      await axios.put(`${API}/players/${playerId}/tier`, { tier: newTier }, { withCredentials: true });
      toast.success(`Player tier updated to ${newTier}`);
      fetchPlayerData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update tier");
    }
  };

  // Derived chart data — memoised to avoid rebuilding on every render
  const eloChartData = useMemo(
    () => eloHistory.map(h => ({
      date: new Date(h.recorded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      elo: h.elo_after,
    })),
    [eloHistory],
  );

  const pointsChartData = useMemo(
    () => rankingHistory.map(h => ({
      date: new Date(h.recorded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      points: h.total_points,
    })),
    [rankingHistory],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Player not found</p>
        <Link to="/players">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Players
          </Button>
        </Link>
      </div>
    );
  }

  const winRate = player.matches_played > 0
    ? ((player.matches_won / player.matches_played) * 100).toFixed(1)
    : 0;

  return (
    <motion.div initial={PAGE_INITIAL} animate={PAGE_ANIMATE} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Link to="/players">
          <Button variant="ghost" className="text-muted-foreground hover:text-foreground" data-testid="back-to-players">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Players
          </Button>
        </Link>
        {(isProfileOwner || user?.is_admin) && (
          <Button
            size="sm" variant="outline"
            className="border-primary/30 text-primary hover:bg-primary/10"
            onClick={() => setLogFriendlyOpen(true)}
            data-testid="log-friendly-btn"
          >
            <HandHeart className="w-4 h-4 mr-1.5" weight="fill" />Log Friendly Match
          </Button>
        )}
      </div>

      <PlayerHeader
        player={player}
        isAdmin={!!user?.is_admin}
        canUploadPhoto={canUploadPhoto}
        uploading={uploading}
        fileInputRef={fileInputRef}
        onPhotoUpload={handlePhotoUpload}
        onTierChange={handleTierChange}
      />

      {/* Pending Match Validations */}
      {pendingValidation.length > 0 && isProfileOwner && (
        <Card className="border-secondary/30 bg-secondary/5" data-testid="pending-validations">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldStar className="w-5 h-5 text-secondary" weight="fill" />
              Confirm Match Results ({pendingValidation.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingValidation.map(m => (
              <div key={m.match_id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-card" data-testid={`validation-${m.match_id}`}>
                <div>
                  <p className="font-medium text-sm">vs {m.opponent_name}</p>
                  <p className="text-xs text-muted-foreground">
                    Score: {m.is_player1 ? `${m.player1_score}-${m.player2_score}` : `${m.player2_score}-${m.player1_score}`}
                    {m.winner_id === playerId ? " (Won)" : " (Lost)"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleValidation(m.match_id, "confirm")} data-testid={`confirm-match-${m.match_id}`}>
                    <CheckCircle className="w-4 h-4 mr-1" />Confirm
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => {
                    const reason = window.prompt("Reason for dispute:");
                    if (reason !== null) handleValidation(m.match_id, "dispute", reason);
                  }} data-testid={`dispute-match-${m.match_id}`}>
                    <XCircle className="w-4 h-4 mr-1" />Dispute
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Upcoming Matches — visible to everyone; claimed owner can request reschedule */}
      {upcomingMatches.length > 0 && (
        <Card className="bg-card border-border" data-testid="upcoming-matches">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <CalendarBlank className="w-5 h-5 text-primary" weight="fill" />
              Upcoming Matches ({upcomingMatches.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {upcomingMatches.map(m => {
              const oppId = m.player1_id === playerId ? m.player2_id : m.player1_id;
              const oppName = m.player1_id === playerId ? m.player2_name : m.player1_name;
              const pendingReq = myRescheduleRequests.find(
                r => r.match_id === m.id && r.status === "pending"
              );
              return (
                <div key={m.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/20 flex-wrap gap-2" data-testid={`upcoming-${m.id}`}>
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-medium text-sm">vs {oppName || "TBD"}</p>
                    <p className="text-xs text-muted-foreground">
                      {m.tournament_name} {m.round ? `· ${m.round.replace(/_/g, " ")}` : ""}
                    </p>
                    <p className="text-xs text-primary mt-0.5">
                      {m.scheduled_date || "TBD"}
                      {m.scheduled_time ? ` at ${m.scheduled_time}` : ""}
                      {m.table_name ? ` · ${m.table_name}` : ""}
                    </p>
                  </div>
                  {isProfileOwner && (
                    <div className="flex flex-wrap gap-1">
                      {(m.scoring_type === "submission"
                        || (m.scoring_type == null && m.tournament_scoring_type !== "live")) && (
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                          onClick={() => setSubmitMatch({ ...m, opponent_name: oppName })}
                          data-testid={`submit-result-open-${m.id}`}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" weight="fill" />Enter Result
                        </Button>
                      )}
                      {pendingReq ? (
                        <Badge variant="outline" className="text-secondary border-secondary/30" data-testid={`request-pending-${m.id}`}>
                          <Clock className="w-3 h-3 mr-1" />Request pending
                        </Badge>
                      ) : (
                        <Button
                          size="sm" variant="outline"
                          className="border-primary/30 text-primary h-7 text-xs"
                          onClick={() => setRequestMatch({ ...m, opponent_id: oppId, opponent_name: oppName })}
                          data-testid={`request-reschedule-${m.id}`}
                        >
                          <Clock className="w-3 h-3 mr-1" />Request Reschedule
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* My reschedule request history (profile owner only) */}
      {isProfileOwner && myRescheduleRequests.length > 0 && (
        <Card className="bg-card border-border" data-testid="my-reschedule-requests">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-secondary" weight="fill" />
              My Reschedule Requests
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {myRescheduleRequests.slice(0, 5).map(r => (
              <div key={r.id} className="p-3 rounded-lg border border-border bg-muted/10" data-testid={`my-req-${r.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-sm">
                      Original: <span className="text-muted-foreground">{r.original_date || "TBD"} {r.original_time || ""} {r.original_table || ""}</span>
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{r.reason}</p>
                    {r.resolution_note && <p className="text-xs text-destructive mt-0.5">Admin note: {r.resolution_note}</p>}
                  </div>
                  <Badge variant="outline" className={
                    r.status === "approved" ? "text-chart-3 border-chart-3/30"
                    : r.status === "rejected" ? "text-destructive border-destructive/30"
                    : "text-secondary border-secondary/30"
                  }>
                    {r.status}
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <PlayerAchievements achievements={achievements} />

      <PlayerChartsSection eloChartData={eloChartData} pointsChartData={pointsChartData} />

      {/* Break Statistics */}
      {breakStats && (breakStats.total_breaks > 0 || player.highest_break > 0) && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Fire className="w-5 h-5 text-secondary" weight="fill" />
              Break Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <BreakStat value={breakStats?.highest || player.highest_break || 0} label="Highest Break" color="text-secondary" />
              <BreakStat value={breakStats?.centuries || player.century_breaks || 0} label="Centuries (100+)" color="text-primary" />
              <BreakStat value={breakStats?.fifties || 0} label="Half Centuries (50+)" color="text-chart-3" />
              <BreakStat value={breakStats?.total_breaks || 0} label="Total Breaks" />
              <BreakStat value={breakStats?.average?.toFixed(1) || 0} label="Average Break" />
            </div>
            {breakStats?.breaks?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wider">All Recorded Breaks</h4>
                <div className="flex flex-wrap gap-2">
                  {breakStats.breaks.map((b, i) => (
                    <Badge
                      key={`break-${b}-${i}`}
                      className={`text-sm px-3 py-1 ${
                        b >= 100 ? 'bg-secondary/20 text-secondary border-secondary/30'
                        : b >= 50 ? 'bg-primary/20 text-primary border-primary/30'
                        : 'bg-muted/50 text-muted-foreground'
                      }`}
                      variant="outline"
                    >
                      {b}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Performance + Recent Form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader><CardTitle className="font-heading text-lg">Performance</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Win Rate</span>
                <span className="font-medium">{winRate}%</span>
              </div>
              <Progress value={parseFloat(winRate)} className="h-2" />
            </div>
            {analytics && (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Frame Win Rate</span>
                  <span className="font-medium">{analytics.frame_win_ratio}%</span>
                </div>
                <Progress value={analytics.frame_win_ratio} className="h-2" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="text-center p-4 rounded-lg bg-muted/30">
                <p className="text-sm text-muted-foreground">Matches Won</p>
                <p className="text-2xl font-heading font-bold text-primary">{player.matches_won}</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted/30">
                <p className="text-sm text-muted-foreground">Matches Lost</p>
                <p className="text-2xl font-heading font-bold text-destructive">
                  {player.matches_played - player.matches_won}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {analytics?.results_timeline?.length > 0 && (
          <Card className="bg-card border-border" data-testid="recent-form-section">
            <CardHeader><CardTitle className="font-heading text-lg">Recent Form</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-1.5 flex-wrap mb-4">
                {analytics.results_timeline.slice(-10).map((r, i) => (
                  <div
                    key={`form-${r.date}-${i}`}
                    className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold ${
                      r.result === 'W'
                        ? 'bg-primary/20 text-primary border border-primary/30'
                        : 'bg-destructive/20 text-destructive border border-destructive/30'
                    }`}
                  >
                    {r.result}
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {analytics.results_timeline.slice(-5).reverse().map((r, i) => (
                  <div key={`timeline-${r.date}-${i}`} className="flex items-center gap-3 text-sm">
                    <Badge variant={r.result === 'W' ? 'default' : 'destructive'} className="w-8 justify-center text-xs">
                      {r.result}
                    </Badge>
                    <span className="text-muted-foreground">{r.frames_won}-{r.frames_lost}</span>
                    {r.elo_after && <span className="text-xs text-muted-foreground ml-auto">ELO: {r.elo_after}</span>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <PlayerLegacyAchievements playerId={player.id} isAdmin={!!user?.is_admin} />

      <PlayerTournamentHistory playerId={player.id} />

      <PlayerMatchHistory matchHistory={matchHistory} playerId={player.id} />

      <RequestRescheduleDialog
        open={!!requestMatch}
        onOpenChange={(v) => { if (!v) setRequestMatch(null); }}
        match={requestMatch}
        onSubmitted={fetchPlayerData}
      />

      <SubmitResultDialog
        open={!!submitMatch}
        onOpenChange={(v) => { if (!v) setSubmitMatch(null); }}
        match={submitMatch ? {
          ...submitMatch,
          player1_name: submitMatch.player1_name || (submitMatch.player1_id === playerId ? player?.name : submitMatch.opponent_name),
          player2_name: submitMatch.player2_name || (submitMatch.player2_id === playerId ? player?.name : submitMatch.opponent_name),
        } : null}
        onSubmitted={fetchPlayerData}
      />

      <LogFriendlyDialog
        open={logFriendlyOpen}
        onOpenChange={setLogFriendlyOpen}
        defaultPlayer1Id={playerId}
        onCreated={fetchPlayerData}
      />

      <PhotoCropDialog
        file={pendingPhotoFile}
        onClose={() => setPendingPhotoFile(null)}
        onCropped={handleCroppedUpload}
      />
    </motion.div>
  );
}

function BreakStat({ value, label, color = "" }) {
  return (
    <div className="text-center p-3 rounded-lg bg-muted/30">
      <p className={`text-2xl font-heading font-bold ${color}`}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
